import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalSelectDocumentsComponent } from '../../modals/modal-select-documents/modal-select-documents.component';
import { StatementGeneratorService } from '../../services/statement-generator.service';
import { SurveyService } from '../../services/surveys.service';
import { UserService } from '../../services/user.service';
import {
  StatementInputSource,
  GeneratedStatement,
  StatementGenerationRequest,
  StatementProcess,
  PICOInputData,
  ProjectDetailsInputData
} from '../../models/statement.models';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-statement-generator',
  templateUrl: './statement-generator.component.html',
  styleUrls: ['./statement-generator.component.scss']
})
export class StatementGeneratorComponent implements OnInit, OnDestroy {

  @Input() projectId: number;
  @Input() projectData: any;
  @Input() picoData: PICOInputData;

  selectedInputs: StatementInputSource[] = [];
  generatedStatements: GeneratedStatement[] = [];

  isGenerating = false;
  currentProcess: StatementProcess | null = null;
  generationProgress = 0;
  generationMessage = '';

  // Needs input state
  needsInput = false;
  needsInputMessage = '';
  needsInputSuggestions: string[] = [];

  // Auto-save configuration
  // Backend endpoints are now implemented, auto-save is ENABLED
  // - POST /api/projects/{projectId}/statements ✅
  // - GET /api/projects/{projectId}/statements ✅
  enableAutoSave = true;

  // Polling subscription
  private pollingSubscription: Subscription | null = null;

  // Display columns for statements table
  displayedColumns: string[] = ['number', 'statement', 'section', 'comment', 'loe', 'gor', 'references', 'actions'];

  constructor(
    private dialog: MatDialog,
    private statementService: StatementGeneratorService,
    private surveysService: SurveyService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    // Auto-add project details and PICO if available
    this.autoAddProjectContext();

    // Note: Statements are not auto-loaded here.
    // Users can view all past statement generations in the "Storico Generazioni Statement" section.
    // This component is only for creating new generations.
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  /**
   * Automatically add project details and PICO data as input sources
   */
  private autoAddProjectContext(): void {
    console.log('autoAddProjectContext called');
    console.log('projectData:', this.projectData);
    console.log('picoData:', this.picoData);

    if (!this.projectData) {
      console.warn('No projectData available');
      return;
    }

    // ALWAYS add Project Details if projectData exists (removed strict condition)
    const projectDetails: ProjectDetailsInputData = {
      title: this.projectData.title || '',
      description: this.projectData.description || '',
      obiettivi: this.projectData.obiettivi || '',
      contesto_clinico_scientifico: this.projectData.contesto_clinico_scientifico || '',
      faculty: this.projectData.faculty || '',
      board: this.projectData.board || ''
    };

    this.selectedInputs.push({
      id: 'project-details',
      type: 'project_details',
      name: `Project: ${this.projectData.title || 'Untitled'}`,
      data: projectDetails,
      metadata: {
        uploadDate: new Date()
      }
    });
    console.log('Added project details to selectedInputs');

    // Add PICO if available
    if (this.picoData && (this.picoData.population || this.picoData.intervention)) {
      this.selectedInputs.push({
        id: 'pico-data',
        type: 'pico',
        name: 'PICO Question',
        data: this.picoData,
        metadata: {
          uploadDate: new Date()
        }
      });
      console.log('Added PICO to selectedInputs');
    } else {
      console.log('No PICO data to add');
    }

    console.log('Total selectedInputs:', this.selectedInputs.length);
  }

  /**
   * Open document/input selector modal
   */
  openInputSelector(): void {
    const dialogRef = this.dialog.open(ModalSelectDocumentsComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        projectId: this.projectId,
        projectData: this.projectData,
        selectedInputs: this.selectedInputs.filter(
          input => input.type !== 'project_details' && input.type !== 'pico'
        )
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.selectedInputs) {
        // Keep project_details and pico, replace others
        const contextInputs = this.selectedInputs.filter(
          input => input.type === 'project_details' || input.type === 'pico'
        );
        this.selectedInputs = [...contextInputs, ...result.selectedInputs];
      }
    });
  }

  /**
   * Build minimal context with only project and PICO data
   * Used to avoid backend parsing issues while still sending essential info
   */
  private buildMinimalContext(): any {
    console.log('buildMinimalContext called');
    console.log('selectedInputs:', this.selectedInputs);
    console.log('selectedInputs types:', this.selectedInputs.map(i => i.type));

    const context: any = {};

    // Extract project data
    const projectInput = this.selectedInputs.find(i => i.type === 'project_details');
    console.log('Found project_details input:', projectInput);

    if (projectInput && projectInput.data) {
      context.project = {
        title: this.sanitizeString(projectInput.data.title),
        obiettivi: this.sanitizeString(projectInput.data.obiettivi),
        contesto_clinico_scientifico: this.sanitizeString(projectInput.data.contesto_clinico_scientifico),
        faculty: this.sanitizeString(projectInput.data.faculty)
      };
      console.log('Added project to context:', context.project);
    } else {
      console.warn('No project_details found in selectedInputs!');
    }

    // Extract PICO data
    const picoInput = this.selectedInputs.find(i => i.type === 'pico');
    console.log('Found pico input:', picoInput);

    if (picoInput && picoInput.data) {
      context.pico = {
        population: this.sanitizeString(picoInput.data.population),
        intervention: this.sanitizeString(picoInput.data.intervention),
        comparison: this.sanitizeString(picoInput.data.comparison),
        outcome: this.sanitizeString(picoInput.data.outcome)
      };
      console.log('Added pico to context:', context.pico);
    } else {
      console.log('No pico input found in selectedInputs');
    }

    console.log('Built minimal context (project + PICO only):', context);
    return context;
  }

  /**
   * Sanitize string to prevent prompt injection and parsing errors
   */
  private sanitizeString(str: string | null | undefined): string {
    if (!str) return '';

    // Convert to string and trim
    let sanitized = String(str).trim();

    // Remove or escape special characters that could break JSON/prompt parsing
    sanitized = sanitized
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
      .replace(/\\/g, '\\\\') // Escape backslashes
      .replace(/"/g, '\\"') // Escape quotes
      .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines
      .replace(/\s{3,}/g, '  '); // Reduce multiple spaces

    // Limit length to prevent token overflow (max 2000 chars per field)
    if (sanitized.length > 2000) {
      sanitized = sanitized.substring(0, 1997) + '...';
    }

    return sanitized;
  }

  /**
   * Enrich input sources by fetching fresh survey responses from API
   * Backend expects input_sources array with type and data fields
   */
  private async enrichInputSourcesWithSurveyData(): Promise<StatementInputSource[]> {
    const enrichedInputs: StatementInputSource[] = [];

    for (const input of this.selectedInputs) {
      if (input.type === 'survey') {
        // Fetch fresh survey responses from API
        const surveyId = input.metadata?.surveyId || input.data?.id;

        if (surveyId) {
          try {
            const surveyData = await new Promise<any>((resolve, reject) => {
              this.surveysService.surveyResponses(
                surveyId.toString(),
                1,
                10,
                (result: any) => resolve(result),
                (error: any) => reject(error)
              );
            });

            // Enrich the input with complete survey data
            enrichedInputs.push({
              ...input,
              data: {
                ...input.data,
                // Add fetched responses and feedback
                responses: surveyData.responses || surveyData || [],
                questions: surveyData.questions || input.data?.questions || [],
                feedback: this.extractFeedbackFromResponses(surveyData.responses || surveyData || [])
              }
            });
          } catch (error) {
            console.error(`Failed to fetch responses for survey ${surveyId}:`, error);
            // Use existing data if fetch fails
            enrichedInputs.push(input);
          }
        } else {
          // No survey ID, use existing data
          enrichedInputs.push(input);
        }
      } else {
        // Non-survey inputs, use as-is
        enrichedInputs.push(input);
      }
    }

    return enrichedInputs;
  }

  /**
   * Build context object from selected inputs for the backend (DEPRECATED - backend builds context from input_sources)
   * Fetches complete survey data from API
   */
  private async buildGenerationContext_DEPRECATED(): Promise<any> {
    const context: any = {};

    // Extract project data
    const projectInput = this.selectedInputs.find(i => i.type === 'project_details');
    if (projectInput && projectInput.data) {
      context.project = {
        type: 'project_details',
        title: this.sanitizeString(projectInput.data.title),
        obiettivi: this.sanitizeString(projectInput.data.obiettivi),
        contesto_clinico_scientifico: this.sanitizeString(projectInput.data.contesto_clinico_scientifico),
        faculty: this.sanitizeString(projectInput.data.faculty),
        board: this.sanitizeString(projectInput.data.board)
      };
    }

    // Extract PICO data
    const picoInput = this.selectedInputs.find(i => i.type === 'pico');
    if (picoInput && picoInput.data) {
      context.pico = {
        type: 'pico',
        population: this.sanitizeString(picoInput.data.population),
        intervention: this.sanitizeString(picoInput.data.intervention),
        comparison: this.sanitizeString(picoInput.data.comparison),
        outcome: this.sanitizeString(picoInput.data.outcome)
      };
    }

    // Extract PubMed articles
    const pubmedInputs = this.selectedInputs.filter(i => i.type === 'pubmed');
    if (pubmedInputs.length > 0) {
      context.articles = pubmedInputs.map(input => ({
        type: 'pubmed',
        title: this.sanitizeString(input.data?.title || input.name),
        year: this.sanitizeString(input.data?.year),
        abstract: this.sanitizeString(input.data?.abstract),
        pmid: this.sanitizeString(input.data?.pmid),
        url: this.sanitizeString(input.metadata?.url)
      }));
    }

    // Extract literature reviews
    const literatureInputs = this.selectedInputs.filter(i => i.type === 'literature_review');
    if (literatureInputs.length > 0) {
      context.literature = literatureInputs.map(input => ({
        type: 'literature_review',
        summary: this.sanitizeString(input.data?.summary || input.data?.content),
        source: this.sanitizeString(input.name)
      }));
    }

    // Extract surveys with complete data (attendees, feedback, responses)
    // Fetch fresh data from API for each survey
    const surveyInputs = this.selectedInputs.filter(i => i.type === 'survey');
    if (surveyInputs.length > 0) {
      // Fetch all survey responses in parallel
      const surveyPromises = surveyInputs.map(input => {
        return new Promise((resolve, reject) => {
          const surveyId = input.metadata?.surveyId || input.data?.id;

          if (!surveyId) {
            // If no ID, use existing data
            resolve({
              type: 'survey',
              survey_id: null,
              title: this.sanitizeString(input.name),
              summary: this.sanitizeString(input.data?.summary || ''),
              responses: [],
              questions: []
            });
            return;
          }

          // Fetch complete survey responses from API
          this.surveysService.surveyResponses(
            surveyId.toString(),
            1,
            10,
            (result: any) => {
              const surveyData = input.data || {};

              resolve({
                type: 'survey',
                survey_id: surveyId,
                title: this.sanitizeString(surveyData.title || input.name),
                description: this.sanitizeString(surveyData.description),
                status: surveyData.status,

                // Attendees/Members/Participants
                attendees: surveyData.members || surveyData.participants || [],
                participants_count: surveyData.responses_count || surveyData.participants_count || 0,
                completion_rate: surveyData.completion_rate || 0,

                // Responses from API
                responses: result.responses || result || [],

                // Questions and results
                questions: result.questions || surveyData.questions || [],

                // Feedback from responses
                feedback: this.extractFeedbackFromResponses(result.responses || result || []),

                // Summary for context
                summary: this.sanitizeString(
                  surveyData.summary || this.buildSurveySummary(surveyData)
                ),

                // Metadata
                created_at: surveyData.created_at,
                closed_at: surveyData.closed_at
              });
            },
            (error: any) => {
              console.error(`Failed to fetch responses for survey ${surveyId}:`, error);
              // Fall back to existing data on error
              const surveyData = input.data || {};
              resolve({
                type: 'survey',
                survey_id: surveyId,
                title: this.sanitizeString(surveyData.title || input.name),
                description: this.sanitizeString(surveyData.description),
                summary: this.sanitizeString(surveyData.summary || ''),
                responses: surveyData.responses || [],
                questions: surveyData.questions || []
              });
            }
          );
        });
      });

      // Wait for all survey data to be fetched
      context.surveys = await Promise.all(surveyPromises);
    }

    // Extract uploaded PDF documents
    const pdfInputs = this.selectedInputs.filter(i => i.type === 'pdf');
    if (pdfInputs.length > 0) {
      context.documents = pdfInputs.map(input => {
        const content = input.data?.content || input.data?.text || `[PDF: ${input.name}]`;
        // Limit document content to 5000 chars
        return {
          type: 'pdf',
          content: this.sanitizeString(content.substring(0, 5000)),
          name: this.sanitizeString(input.name)
        };
      });
    }

    // Extract GRADE revisions
    const gradeInputs = this.selectedInputs.filter(i => i.type === 'grade');
    if (gradeInputs.length > 0) {
      context.grade = gradeInputs.map(input => {
        const summary = input.data?.summary || this.buildGradeSummary(input.data);
        return {
          type: 'grade',
          summary: this.sanitizeString(summary),
          revision_id: input.data?.id || null,
          title: this.sanitizeString(input.data?.title || input.name)
        };
      });
    }

    // Clean up empty fields to avoid sending unnecessary data
    Object.keys(context).forEach(key => {
      if (Array.isArray(context[key]) && context[key].length === 0) {
        delete context[key];
      } else if (typeof context[key] === 'object' && context[key] !== null) {
        // Check if all values in object are empty strings
        const allEmpty = Object.values(context[key]).every(val => val === '' || val === null);
        if (allEmpty) {
          delete context[key];
        }
      }
    });

    console.log('✅ Built context with ONLY selected input types:');
    console.log('   - project:', !!context.project ? '✓ INCLUDED' : '✗ not selected');
    console.log('   - pico:', !!context.pico ? '✓ INCLUDED' : '✗ not selected');
    console.log('   - surveys:', context.surveys ? `✓ INCLUDED (${context.surveys.length})` : '✗ not selected');
    console.log('   - articles (pubmed):', context.articles ? `✓ INCLUDED (${context.articles.length})` : '✗ not selected');
    console.log('   - documents (pdf):', context.documents ? `✓ INCLUDED (${context.documents.length})` : '✗ not selected');
    console.log('   - literature:', context.literature ? `✓ INCLUDED (${context.literature.length})` : '✗ not selected');
    console.log('   - grade:', context.grade ? `✓ INCLUDED (${context.grade.length})` : '✗ not selected');
    console.log('Full context object:', context);

    return context;
  }

  /**
   * Extract feedback from survey responses
   */
  private extractFeedbackFromResponses(responses: any[]): string[] {
    if (!responses || !Array.isArray(responses)) return [];

    const feedback: string[] = [];

    responses.forEach((response: any) => {
      // Check for feedback field directly
      if (response.feedback) {
        feedback.push(this.sanitizeString(response.feedback));
      }

      // Check for answers array with feedback
      if (response.answers && Array.isArray(response.answers)) {
        response.answers.forEach((answer: any) => {
          if (answer.feedback) {
            feedback.push(this.sanitizeString(answer.feedback));
          }
        });
      }

      // Check for text-type responses that might be feedback
      if (response.answer_value && typeof response.answer_value === 'string') {
        feedback.push(this.sanitizeString(response.answer_value));
      }
    });

    return feedback.filter(f => f && f.length > 0);
  }

  /**
   * Build a summary string from survey data
   */
  private buildSurveySummary(surveyData: any): string {
    if (!surveyData) return '';

    let summary = `Survey: ${surveyData.title || 'Untitled'}\n`;
    if (surveyData.description) {
      summary += `Description: ${surveyData.description}\n`;
    }
    if (surveyData.responses && surveyData.responses.length > 0) {
      summary += `Responses: ${surveyData.responses.length}\n`;
      summary += 'Key findings:\n';
      surveyData.responses.slice(0, 5).forEach((response: any) => {
        summary += `- ${response.question_text}: ${JSON.stringify(response.answers)}\n`;
      });
    }
    return summary;
  }

  /**
   * Build a summary string from GRADE data
   */
  private buildGradeSummary(gradeData: any): string {
    if (!gradeData) return '';

    let summary = `GRADE Revision: ${gradeData.title || 'Untitled'}\n`;
    if (gradeData.description) {
      summary += `Description: ${gradeData.description}\n`;
    }
    if (gradeData.quality) {
      summary += `Quality of Evidence: ${gradeData.quality}\n`;
    }
    if (gradeData.recommendations) {
      summary += `Recommendations:\n${gradeData.recommendations}\n`;
    }
    return summary;
  }

  /**
   * Generate statements from selected inputs
   */
  async generateStatements(): Promise<void> {
    console.log('=== GENERATE STATEMENTS CALLED ===');
    console.log('selectedInputs at generation time:', this.selectedInputs);
    console.log('selectedInputs types:', this.selectedInputs.map(i => i.type));
    console.log('Has project_details?', this.selectedInputs.some(i => i.type === 'project_details'));
    console.log('Has pico?', this.selectedInputs.some(i => i.type === 'pico'));

    if (this.selectedInputs.length === 0) {
      this.showMessage('statement-generator.no-inputs-selected', 'warning');
      return;
    }

    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.showMessage('generic.error.no-organization', 'error');
      return;
    }

    this.isGenerating = true;
    this.generationProgress = 0;
    this.generationMessage = this.translate.instant('statement-generator.preparing');

    // Enrich input sources with fresh survey data from API
    // Backend will build context from input_sources array
    console.log('🔄 Fetching survey responses from API...');
    const enrichedInputSources = await this.enrichInputSourcesWithSurveyData();
    console.log('✅ Input sources enriched with survey data from API');

    const request: StatementGenerationRequest = {
      project_id: this.projectId,
      organization_id: organizationId,
      input_sources: enrichedInputSources,  // Send enriched input sources, backend builds context
      generation_options: {
        include_pico: this.selectedInputs.some(i => i.type === 'pico'),
        include_project_details: this.selectedInputs.some(i => i.type === 'project_details'),
        language: this.translate.currentLang || 'it'
      }
    };

    console.log('Sending statement generation request:', {
      project_id: request.project_id,
      organization_id: request.organization_id,
      input_sources_count: request.input_sources.length,
      input_sources_types: request.input_sources.map(i => i.type),
      survey_inputs: request.input_sources
        .filter(i => i.type === 'survey')
        .map(i => ({
          id: i.data?.id,
          title: i.data?.title,
          has_responses: !!i.data?.responses,
          responses_count: i.data?.responses?.length || 0,
          has_feedback: !!i.data?.feedback,
          feedback_count: i.data?.feedback?.length || 0
        })),
      language: request.generation_options?.language
    });
    console.log('Full input_sources being sent:', JSON.stringify(enrichedInputSources, null, 2));

    this.statementService.generateStatements(
      request,
      (response) => {
        this.currentProcess = {
          id: response.process_id,
          project_id: this.projectId,
          organization_id: organizationId,
          status: response.status,
          input_sources_count: this.selectedInputs.length,
          message: response.message,
          created_at: response.created_at,
          updated_at: response.created_at
        };

        // Start polling for status
        this.startPolling(response.process_id);
      },
      (error) => {
        console.error('Error generating statements:', error);
        this.isGenerating = false;
        this.showMessage('statement-generator.generation-failed', 'error');
      }
    );
  }

  /**
   * Poll for statement generation status
   */
  private startPolling(processId: string): void {
    this.generationMessage = this.translate.instant('statement-generator.processing');
    this.generationProgress = 10;

    this.pollingSubscription = this.statementService.pollProcessStatus(processId).subscribe({
      next: (process) => {
        this.currentProcess = process;
        this.generationProgress = process.progress_percentage || 50;
        this.generationMessage = process.message || this.translate.instant('statement-generator.processing');

        if (process.status === 'completed') {
          // Unsubscribe immediately to prevent multiple calls
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onGenerationCompleted(processId);
        } else if (process.status === 'failed') {
          // Unsubscribe on failure too
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onGenerationFailed(process.error_message);
        } else if (process.status === 'needs_input') {
          // Unsubscribe - waiting for user to add more data
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onNeedsInput(process.message, process.error_message);
        }
      },
      error: (error) => {
        console.error('Polling error:', error);
        if (this.pollingSubscription) {
          this.pollingSubscription.unsubscribe();
          this.pollingSubscription = null;
        }
        this.isGenerating = false;
        this.showMessage('statement-generator.polling-failed', 'error');
      }
    });
  }

  /**
   * Handle successful statement generation
   */
  private onGenerationCompleted(processId: string): void {
    this.generationProgress = 90;
    this.generationMessage = this.translate.instant('statement-generator.loading-statements');

    this.statementService.getGeneratedStatements(
      processId,
      (statements) => {
        this.generatedStatements = statements;
        this.generationProgress = 95;

        // Automatically save statements to database (only if enabled)
        if (this.enableAutoSave && this.projectId && statements.length > 0) {
          this.generationMessage = this.translate.instant('statement-generator.saving-statements');
          this.autoSaveStatements(statements);
        } else {
          this.isGenerating = false;
          this.generationProgress = 100;
          this.showMessage('statement-generator.generation-success', 'success');
        }
      },
      (error) => {
        console.error('Error loading statements:', error);
        this.isGenerating = false;
        this.showMessage('statement-generator.loading-failed', 'error');
      }
    );
  }

  /**
   * Automatically save generated statements to database
   * NOTE: This requires backend endpoint POST /api/projects/{projectId}/statements
   */
  private autoSaveStatements(statements: GeneratedStatement[]): void {
    // Set a timeout to prevent infinite hanging
    const saveTimeout = setTimeout(() => {
      console.warn('Auto-save timeout reached');
      this.isGenerating = false;
      this.generationProgress = 100;
      this.showMessage('statement-generator.generation-success-save-timeout', 'warning');
    }, 10000); // 10 second timeout

    this.statementService.saveStatementsToProject(
      this.projectId,
      statements,
      (result) => {
        clearTimeout(saveTimeout);
        console.log('Statements automatically saved:', result);

        // Update statements with IDs from database if returned
        if (result && result.statements) {
          this.generatedStatements = result.statements;
        }

        this.isGenerating = false;
        this.generationProgress = 100;
        this.showMessage('statement-generator.generation-and-save-success', 'success');
      },
      (error) => {
        clearTimeout(saveTimeout);
        console.error('Error auto-saving statements:', error);
        this.isGenerating = false;
        this.generationProgress = 100;
        // Still show success for generation, but warn about save failure
        this.showMessage('statement-generator.generation-success-save-failed', 'warning');
      }
    );
  }

  /**
   * Handle failed statement generation
   */
  private onGenerationFailed(errorMessage?: string): void {
    this.isGenerating = false;
    this.generationProgress = 0;
    const message = errorMessage || this.translate.instant('statement-generator.generation-failed');
    this.showMessage(message, 'error');
  }

  /**
   * Handle 'needs_input' status - AI needs more data to generate statements
   */
  private onNeedsInput(message?: string, errorMessageJson?: string): void {
    this.isGenerating = false;
    this.generationProgress = 0;
    this.needsInput = true;

    // Parse error_message JSON to extract details and suggestions
    if (errorMessageJson) {
      try {
        const errorData = JSON.parse(errorMessageJson);
        this.needsInputMessage = errorData.message || message || this.translate.instant('statement-generator.needs-more-data');
        this.needsInputSuggestions = errorData.suggestions || [];
      } catch (e) {
        console.error('Failed to parse error_message JSON:', e);
        this.needsInputMessage = message || this.translate.instant('statement-generator.needs-more-data');
        this.needsInputSuggestions = [];
      }
    } else {
      this.needsInputMessage = message || this.translate.instant('statement-generator.needs-more-data');
      this.needsInputSuggestions = [];
    }

    console.log('⚠️ Needs more input:', this.needsInputMessage);
    console.log('💡 Suggestions:', this.needsInputSuggestions);

    // Show informational message (not error - it's a request for more data)
    this.showMessage(this.needsInputMessage, 'warning');
  }

  /**
   * Retry generation after adding more data
   */
  retryGeneration(): void {
    this.needsInput = false;
    this.needsInputMessage = '';
    this.needsInputSuggestions = [];
    // User can now add more sources and click generate again
  }

  /**
   * Remove an input source
   */
  removeInput(index: number): void {
    const input = this.selectedInputs[index];

    // Prevent removal of auto-added context
    if (input.type === 'project_details' || input.type === 'pico') {
      this.showMessage('statement-generator.cannot-remove-context', 'warning');
      return;
    }

    this.selectedInputs.splice(index, 1);
    this.generatedStatements = [];
  }

  /**
   * Clear all selected inputs (except project context)
   */
  clearAllInputs(): void {
    // Keep project_details and pico
    this.selectedInputs = this.selectedInputs.filter(
      input => input.type === 'project_details' || input.type === 'pico'
    );
    this.generatedStatements = [];
  }

  /**
   * Get icon for input type
   */
  getInputIcon(type: string): string {
    const icons = {
      'survey': 'poll',
      'pdf': 'picture_as_pdf',
      'pubmed': 'article',
      'pico': 'psychology',
      'project_details': 'folder',
      'literature_review': 'library_books'
    };
    return icons[type] || 'insert_drive_file';
  }

  /**
   * Get badge class for Level of Evidence
   */
  getLoEBadgeClass(loe: string): string {
    const level = loe.toLowerCase();
    if (level === '1a') return 'loe-1a';
    if (level === '1b') return 'loe-1b';
    if (level === '2a') return 'loe-2a';
    if (level === '2b') return 'loe-2b';
    return 'loe-3';
  }

  /**
   * Get badge class for Grade of Recommendation
   */
  getGoRBadgeClass(gor: string): string {
    const grade = gor.toUpperCase();
    return `gor-${grade.toLowerCase()}`;
  }

  /**
   * Load stored statements from database
   */
  loadStoredStatements(): void {
    if (!this.projectId) {
      return;
    }

    this.statementService.getProjectStatements(
      this.projectId,
      (statements) => {
        if (statements && statements.length > 0) {
          this.generatedStatements = statements;
          console.log('Loaded stored statements:', statements);
        }
      },
      (error) => {
        console.error('Error loading stored statements:', error);
        // Don't show error message on init - statements might not exist yet
      }
    );
  }

  /**
   * Save statements to project (manual save)
   */
  saveStatements(): void {
    if (!this.projectId || this.generatedStatements.length === 0) {
      return;
    }

    this.statementService.saveStatementsToProject(
      this.projectId,
      this.generatedStatements,
      (result) => {
        this.showMessage('statement-generator.statements-saved', 'success');
        // Reload to get updated IDs
        this.loadStoredStatements();
      },
      (error) => {
        console.error('Error saving statements:', error);
        this.showMessage('statement-generator.save-failed', 'error');
      }
    );
  }

  /**
   * Update a specific statement
   */
  updateStatement(statement: GeneratedStatement, updates: Partial<GeneratedStatement>): void {
    if (!statement.id) {
      console.error('Cannot update statement without ID');
      return;
    }

    this.statementService.updateStatement(
      statement.id,
      updates,
      (updatedStatement) => {
        // Update in local array
        const index = this.generatedStatements.findIndex(s => s.id === statement.id);
        if (index !== -1) {
          this.generatedStatements[index] = updatedStatement;
        }
        this.showMessage('statement-generator.statement-updated', 'success');
      },
      (error) => {
        console.error('Error updating statement:', error);
        this.showMessage('statement-generator.update-failed', 'error');
      }
    );
  }

  /**
   * Delete a statement
   */
  deleteStatement(statement: GeneratedStatement): void {
    // Confirm deletion
    const statementPreview = statement.statement_text?.substring(0, 50) || 'questo statement';
    const confirmMessage = `Sei sicuro di voler eliminare questo statement?\n\n"${statementPreview}${statement.statement_text && statement.statement_text.length > 50 ? '...' : ''}"\n\nQuesta azione non può essere annullata.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    // If it's an unsaved statement (no ID from database)
    if (!statement.id && statement.id !== 0) {
      // Just remove from local array
      console.log('✅ Removing unsaved statement from local list');
      this.generatedStatements = this.generatedStatements.filter(s => s !== statement);
      this.showMessage('Statement rimosso dalla lista', 'success');
      return;
    }

    // Delete from database
    console.log(`🗑️ Deleting statement ID: ${statement.id}`);
    this.statementService.deleteStatement(
      statement.id,
      (result) => {
        // Remove from local array
        const previousCount = this.generatedStatements.length;
        this.generatedStatements = this.generatedStatements.filter(s => s.id !== statement.id);
        console.log(`✅ Statement deleted from database. Previous count: ${previousCount}, New count: ${this.generatedStatements.length}`);
        this.showMessage('Statement eliminato con successo', 'success');
      },
      (error) => {
        console.error('❌ Error deleting statement:', error);
        this.showMessage('Errore durante l\'eliminazione dello statement', 'error');
      }
    );
  }

  /**
   * Prompt user for AI-assisted update
   */
  promptAIUpdate(statement: GeneratedStatement): void {
    const promptMessage = this.translate.instant('statement-generator.ai-update-prompt-message');
    const updatePrompt = window.prompt(promptMessage);

    if (updatePrompt && updatePrompt.trim().length > 0) {
      this.updateStatementWithAI(statement, updatePrompt);
    }
  }

  /**
   * Update statement with AI assistance
   */
  updateStatementWithAI(statement: GeneratedStatement, updatePrompt: string): void {
    if (!statement.id) {
      console.error('Cannot update statement without ID');
      this.showMessage('statement-generator.no-id-error', 'error');
      return;
    }

    if (!updatePrompt || updatePrompt.trim().length === 0) {
      this.showMessage('statement-generator.empty-prompt-error', 'error');
      return;
    }

    // Show loading state
    this.showMessage('statement-generator.ai-updating', 'success');

    this.statementService.updateStatementWithAI(
      statement.id,
      updatePrompt,
      (updatedStatement) => {
        // Update in local array
        const index = this.generatedStatements.findIndex(s => s.id === statement.id);
        if (index !== -1) {
          this.generatedStatements[index] = updatedStatement;
        }
        this.showMessage('statement-generator.ai-update-success', 'success');
      },
      (error) => {
        console.error('Error updating statement with AI:', error);
        this.showMessage('statement-generator.ai-update-failed', 'error');
      }
    );
  }

  /**
   * Show snackbar message
   */
  private showMessage(messageKey: string, type: 'success' | 'error' | 'warning'): void {
    const message = this.translate.instant(messageKey);
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Check if has any user-selected inputs (excluding context)
   */
  get hasUserInputs(): boolean {
    return this.selectedInputs.some(
      input => input.type !== 'project_details' && input.type !== 'pico'
    );
  }

  /**
   * Get input type label
   */
  getInputTypeLabel(type: string): string {
    const labels = {
      'survey': this.translate.instant('statement-generator.input-types.survey'),
      'pdf': this.translate.instant('statement-generator.input-types.pdf'),
      'pubmed': this.translate.instant('statement-generator.input-types.pubmed'),
      'pico': this.translate.instant('statement-generator.input-types.pico'),
      'project_details': this.translate.instant('statement-generator.input-types.project-details'),
      'literature_review': this.translate.instant('statement-generator.input-types.literature-review')
    };
    return labels[type] || type;
  }
}
